import { useLockedAction } from "@components/plan-subscription-lock";
import { useAppSelector, type RootState } from "@store";
import { getSlidesLength } from "@store/story/selectors";
import { useCallback, useMemo } from "react";
import { useStore } from "react-redux";

export const useSlidesLimit = () => {
  const {
    value: slidesLimit,
    plan,
    isLocked,
    onLockedAction,
  } = useLockedAction({
    feature: "slides",
  });

  const slidesLength = useAppSelector(getSlidesLength);
  const areSlidesReachedLimit = useMemo(() => {
    if (typeof slidesLimit !== "number") return false;
    const status: boolean = slidesLength >= slidesLimit;
    return status;
  }, [slidesLength, slidesLimit]);

  const effectiveSlidesLimit = slidesLimit || 999;

  // The ONE definition of "adding `count` more slides would break the
  // plan's limit", so every add path asks the same question. It was
  // previously inlined in use-add-slides.tsx's handleAddSlides only, and
  // the AI agent's insert-slide handler -- which dispatches createEntity
  // itself -- had no check at all, so the agent could insert past a limit
  // the editor's own add-slide button refuses at.
  // The typeof guard is not defensive dressing: useLockedAction's `value`
  // is a plan-config field typed `string | number | boolean | undefined`,
  // and `|| 999` above only replaces the falsy cases. Kept exactly as
  // use-add-slides.tsx's own inlined check had it, so extracting this
  // changes no behaviour.
  // Reads the live count: the agent calls this several times in one turn,
  // and the render-time value misses slides it added earlier in that turn.
  const store = useStore<RootState>();
  const wouldExceedLimit = useCallback(
    (count: number): boolean =>
      isLocked &&
      typeof effectiveSlidesLimit === "number" &&
      getSlidesLength(store.getState()) + count > effectiveSlidesLimit,
    [isLocked, store, effectiveSlidesLimit],
  );

  return {
    isLimitReached: areSlidesReachedLimit,
    slidesLength,
    slidesLimit: effectiveSlidesLimit,
    plan,
    isLocked,
    lockAction: onLockedAction,
    wouldExceedLimit,
  };
};
